/**
 * 型號基準的驗證。前後端共用。
 *
 * 為什麼要有這個檔案：型號基準是 R2 分攤的依據（`panelBasis` → `specOf`），
 * 組成比例打錯一個小數點，之後每一片板子的分攤數字都會歪，而且歪的方式很難看出來——
 * 帳面上每一張工單都「算得出來」，只是全部都錯。所以寧可在寫進事件流之前擋下來。
 *
 * §7.5 的做法一樣適用：前端拿同一份程式碼做即時回饋（打字就看到結果），
 * 後端再驗一次才是真的防線。前端擋得住不代表後端可以不驗——API 是公開的。
 */
/** 組成比例的加總容差。1.0 ± 0.005 —— 讓 0.722 + 0.122 + 0.020 + 0.136 這種寫法過得去。 */
export const COMPOSITION_SUM_TOLERANCE = 0.005;
const MODEL_CODE_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{1,23}$/;
/**
 * 驗證一筆型號基準。回傳空陣列表示沒問題。
 *
 * 刻意回傳「欄位 + 訊息」而不是丟例外：畫面要能把錯誤標在對應的欄位旁邊，
 * 一次看到全部問題，而不是修一個才看到下一個。
 */
export function validateModelSpec(spec, opts) {
    const issues = [];
    const add = (field, message) => issues.push({ field, message });
    const provisional = opts.status === 'provisional';
    // ── 型號代碼 ──────────────────────────────────────────────────────────
    const code = typeof spec.modelCode === 'string' ? spec.modelCode.trim() : '';
    if (!opts.isEdit) {
        if (code === '') {
            add('modelCode', '型號代碼必填。');
        }
        else if (!MODEL_CODE_RE.test(code)) {
            add('modelCode', '型號代碼只能用英數字、減號、底線，2–24 個字，且要以英數字開頭。');
        }
        else if ((opts.existingModelCodes ?? []).some((x) => x.toLowerCase() === code.toLowerCase())) {
            add('modelCode', `型號代碼「${code}」已經存在。代碼是型號的身分，不能重複。`);
        }
    }
    // ── 名稱 ──────────────────────────────────────────────────────────────
    const label = typeof spec.label === 'string' ? spec.label.trim() : '';
    if (label === '')
        add('label', '名稱必填。這是畫面上顯示的文字。');
    else if (label.length > 60)
        add('label', '名稱請控制在 60 個字以內。');
    // ── 品牌／製造商（選填） ───────────────────────────────────────────────
    const maker = spec.manufacturer;
    if (maker !== undefined && maker !== null && typeof maker !== 'string') {
        add('manufacturer', '品牌／製造商要是文字。');
    }
    else if (typeof maker === 'string' && maker.trim().length > 60) {
        add('manufacturer', '品牌／製造商請控制在 60 個字以內。');
    }
    // ── 額定功率（選填） ──────────────────────────────────────────────────
    const watts = spec.ratedW;
    if (watts !== undefined && watts !== null && watts !== 0) {
        if (typeof watts !== 'number' || !Number.isFinite(watts)) {
            add('ratedW', '額定功率要是數字。');
        }
        else if (watts <= 0 || watts > 2000) {
            add('ratedW', '額定功率要在 1 到 2000 W 之間。');
        }
    }
    // ── 片數 ──────────────────────────────────────────────────────────────
    const cells = spec.cellCount;
    if (cells === undefined || cells === null || (provisional && cells === 0)) {
        if (!provisional)
            add('cellCount', '電池片數必填。覆核前要把它補上。');
    }
    else if (typeof cells !== 'number' || !Number.isFinite(cells)) {
        add('cellCount', '電池片數要是數字。');
    }
    else if (!Number.isInteger(cells)) {
        add('cellCount', '電池片數要是整數。');
    }
    else if (cells <= 0 || cells > 400) {
        add('cellCount', '電池片數要在 1 到 400 之間。');
    }
    // ── 總重 ──────────────────────────────────────────────────────────────
    const kg = spec.totalKg;
    if (kg === undefined || kg === null || (provisional && kg === 0)) {
        if (!provisional)
            add('totalKg', '單片總重必填。覆核前要把它補上。');
    }
    else if (typeof kg !== 'number' || !Number.isFinite(kg)) {
        add('totalKg', '單片總重要是數字。');
    }
    else if (kg <= 0) {
        add('totalKg', '單片總重要大於 0。');
    }
    else if (kg > 2000) {
        add('totalKg', '單片總重超過 2000 kg，請確認單位是公斤而不是公克。');
    }
    // ── 組成比例 ──────────────────────────────────────────────────────────
    const comp = spec.composition;
    if (!comp || typeof comp !== 'object' || Array.isArray(comp)) {
        if (!provisional)
            add('composition', '組成比例必填。覆核前要把它補上。');
        return issues;
    }
    const entries = Object.entries(comp);
    if (entries.length === 0) {
        if (!provisional)
            add('composition', '至少要填一項組成比例。覆核前要把它補上。');
        return issues;
    }
    let sum = 0;
    for (const [material, ratio] of entries) {
        if (!opts.knownMaterialCodes.includes(material)) {
            add(`composition.${material}`, `料號字典裡沒有「${material}」這個物料代碼。`);
            continue;
        }
        if (typeof ratio !== 'number' || !Number.isFinite(ratio)) {
            add(`composition.${material}`, '比例要是數字。');
            continue;
        }
        if (ratio <= 0 || ratio > 1) {
            add(`composition.${material}`, '比例要大於 0 且不超過 1（0.722 表示 72.2%）。');
            continue;
        }
        sum += ratio;
    }
    // 暫定型號允許只填知道的那幾項——反正它不參與計算（specIsUsable 會擋掉）。
    // 覆核時這條一定要過，那才是數字開始生效的時刻。
    if (!provisional && Math.abs(sum - 1) > COMPOSITION_SUM_TOLERANCE) {
        add('composition', `組成比例加總是 ${(sum * 100).toFixed(1)}%，必須是 100%（容差 ±${(COMPOSITION_SUM_TOLERANCE * 100).toFixed(1)}%）。` +
            '拆出來的每一種料加起來就是整片板子，湊不滿或超過都表示有一項填錯了。');
    }
    return issues;
}
/** 驗證整包型號基準（給整批覆蓋的那支 API 用）。 */
export function validateModelSpecs(specs, knownMaterialCodes) {
    if (!Array.isArray(specs)) {
        return [{ field: 'modelSpecs', message: '型號基準要是一個陣列。' }];
    }
    if (specs.length === 0) {
        return [
            {
                field: 'modelSpecs',
                message: '型號基準不能清空。未辨識型號的板子用的是全型號平均值，沒有型號就算不出基準。',
            },
        ];
    }
    if (!specs.some((x) => (x?.status ?? 'confirmed') === 'confirmed')) {
        return [
            {
                field: 'modelSpecs',
                message: '至少要保留一個已覆核的型號。暫定型號不參與計算，全部都是暫定的話，' +
                    '未辨識板子的平均基準會變成 0，所有分攤數字都會歸零。',
            },
        ];
    }
    const issues = [];
    const seen = [];
    specs.forEach((spec, i) => {
        const one = validateModelSpec(spec, {
            knownMaterialCodes,
            existingModelCodes: seen,
            status: spec?.status ?? 'confirmed',
        });
        for (const issue of one)
            issues.push({ field: `modelSpecs[${i}].${issue.field}`, message: issue.message });
        const code = spec?.modelCode;
        if (typeof code === 'string')
            seen.push(code);
    });
    return issues;
}
/** 把驗證結果壓成一段可以直接丟給使用者看的文字。 */
export function describeIssues(issues) {
    return issues.map((i) => `${i.field}：${i.message}`).join('\n');
}
//# sourceMappingURL=modelSpec.js.map